import { registerInstrumentations } from '@opentelemetry/instrumentation';
import {
  WebTracerProvider,
  ConsoleSpanExporter,
  SimpleSpanProcessor,
  BatchSpanProcessor,
} from '@opentelemetry/sdk-trace-web';
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { Resource } from '@opentelemetry/resources';
import { B3Propagator } from '@opentelemetry/propagator-b3';
import { SemanticResourceAttributes } from '@opentelemetry/semantic-conventions';
import { environment } from './environments/environment';
import { BUILD_INFO } from './app/services/build-info';
import type { ReadableSpan, Span, SpanProcessor } from '@opentelemetry/sdk-trace-web';

/**
 * Nombres décimaux convertis en texte avant l'envoi. OpenObserve REJETTE tout attribut
 * décimal au format OTLP JSON ({"doubleValue": 1.5} → « invalid type: map, expected
 * f64 ») et avec lui le lot entier : depuis le 05/10, chaque bilan perf (minutes d'usage
 * décimales) faisait perdre tous les spans de son lot (~1 300 lots par jour). Il stocke
 * de toute façon les attributs en texte.
 */
class SansDecimaux implements SpanProcessor {
  onStart(_span: Span): void {}
  onEnd(span: ReadableSpan): void {
    try {
      const convertir = (attributs: Record<string, unknown> | undefined) => {
        if (!attributs) return;
        for (const cle of Object.keys(attributs)) {
          const v = attributs[cle];
          if (typeof v === 'number' && !Number.isInteger(v)) attributs[cle] = String(v);
        }
      };
      convertir(span.attributes as Record<string, unknown>);
      for (const evenement of span.events ?? []) convertir(evenement.attributes as Record<string, unknown>);
    } catch {
      // Télémétrie seulement.
    }
  }
  forceFlush(): Promise<void> { return Promise.resolve(); }
  shutdown(): Promise<void> { return Promise.resolve(); }
}

/**
 * ⚠️ Ce fichier est importé en PREMIER par main.ts, avant le bootstrap Angular.
 * Une exception levée ici empêcherait l'application entière de démarrer (écran
 * blanc pour tous les utilisateurs). La télémétrie n'étant jamais critique, toute
 * l'initialisation est isolée : en cas d'échec, l'app démarre simplement sans elle.
 */
function initTelemetry(): WebTracerProvider {
  const resource = Resource.default().merge(
    new Resource({
      [SemanticResourceAttributes.SERVICE_NAME]: 'atlas-v2-web',
      [SemanticResourceAttributes.SERVICE_VERSION]: BUILD_INFO.version,
      [SemanticResourceAttributes.DEPLOYMENT_ENVIRONMENT]: BUILD_INFO.environment,
      'app.build': BUILD_INFO.build,
      'app.commit': BUILD_INFO.commit,
      'app.branch': BUILD_INFO.branch,
      'app.build_date': BUILD_INFO.date,
    })
  );

  const provider = new WebTracerProvider({ resource });

  // Console : uniquement hors production. Un SimpleSpanProcessor logge CHAQUE span de
  // façon synchrone — coût inutile sur les postes des techniciens.
  if (!environment.production) {
    provider.addSpanProcessor(new SimpleSpanProcessor(new ConsoleSpanExporter()));
  }

  // AVANT l'envoi groupé (les processeurs sont appelés dans l'ordre d'ajout).
  provider.addSpanProcessor(new SansDecimaux());

  // Les traces partent vers l'API, qui les relaie à OpenObserve avec le credential
  // côté serveur (cf. TelemetryController). Envoyer directement à OpenObserve
  // exposerait un token d'ingestion dans le bundle de la PWA.
  // (L'ancienne URL pointait vers le collecteur SigNoz, hors service depuis la
  // migration OpenObserve : toute la télémétrie front se perdait.)
  provider.addSpanProcessor(
    new BatchSpanProcessor(
      new OTLPTraceExporter({
        url: `${environment.baseUrl}telemetry/traces`,
      })
    )
  );

  provider.register({
    propagator: new B3Propagator(),
  });

  // VOLUME : on ne garde que `document-load` (1 span par chargement d'app).
  // Les trois autres généraient un span par clic et par requête HTTP — soit des
  // dizaines de milliers de spans par jour et par technicien, pour une information
  // que l'API trace déjà côté serveur. On ne veut remonter que les ANOMALIES
  // (long tasks, pression mémoire, erreurs), émises par PerformanceMonitorService
  // et le gestionnaire d'erreurs global : un poste sain n'émet quasiment rien.
  registerInstrumentations({
    instrumentations: [
      getWebAutoInstrumentations({
        '@opentelemetry/instrumentation-document-load': {},
        '@opentelemetry/instrumentation-user-interaction': { enabled: false },
        '@opentelemetry/instrumentation-fetch': { enabled: false },
        '@opentelemetry/instrumentation-xml-http-request': { enabled: false },
      }),
    ],
  });

  return provider;
}

let provider: WebTracerProvider | null = null;
try {
  provider = initTelemetry();
} catch (error) {
  // L'application doit démarrer même sans télémétrie.
  console.warn('Télémétrie non initialisée (sans impact sur l\'application):', error);
}

/**
 * Envoi IMMÉDIAT des spans en attente quand l'appli passe en arrière-plan. Les spans
 * partent par lots, quelques secondes après leur création ; or iOS suspend la page dès
 * qu'elle est masquée : le lot ne partait qu'au réveil, parfois des heures plus tard, et
 * OpenObserve le rejetait comme trop ancien (~1 300 lots perdus par jour depuis le 05/10,
 * dont les bilans perf émis justement à la mise en arrière-plan). L'export passe par
 * sendBeacon, qui survit au masquage. Différé d'un tour pour partir APRÈS les autres
 * écouteurs (le bilan est émis par l'un d'eux).
 */
if (provider && typeof document !== 'undefined') {
  const viderFile = () => setTimeout(() => { provider?.forceFlush().catch(() => {}); }, 0);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') viderFile();
  });
  window.addEventListener('pagehide', viderFile);
}

// Helper to get user context
function getUserContext(): { username?: string; email?: string } {
  try {
    const stable = localStorage.getItem('sentryUser');
    if (stable) {
      try {
        const u = JSON.parse(stable);
        return { username: u?.username || u?.name, email: u?.email };
      } catch {
        return { username: stable };
      }
    }
    const keys = ['msal.active-account', 'username', 'userName', 'user'];
    for (const k of keys) {
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      try {
        const obj = JSON.parse(raw);
        const username = obj?.username || obj?.userName || obj?.name || obj?.displayName || obj?.preferred_username;
        const email = obj?.email || obj?.preferred_username;
        if (username || email) return { username, email };
      } catch {}
    }
  } catch {}
  return {};
}

// Capture global JavaScript errors
window.addEventListener('error', (event) => {
  if (!provider) return;
  const tracer = provider.getTracer('atlas-v2-web');
  const userContext = getUserContext();
  const attributes: any = {
    'error.type': 'JavaScriptError',
    'error.message': event.message,
    'error.filename': event.filename || '',
    'error.lineno': event.lineno || 0,
    'error.colno': event.colno || 0,
  };
  if (userContext.username) attributes['user.name'] = userContext.username;
  if (userContext.email) attributes['user.email'] = userContext.email;
  
  const span = tracer.startSpan('window.error', { attributes });
  if (event.error) span.recordException(event.error);
  span.end();
});

// Capture unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  if (!provider) return;
  const tracer = provider.getTracer('atlas-v2-web');
  const userContext = getUserContext();
  const attributes: any = {
    'error.type': 'UnhandledPromiseRejection',
    'error.message': event.reason?.message || String(event.reason),
  };
  if (userContext.username) attributes['user.name'] = userContext.username;
  if (userContext.email) attributes['user.email'] = userContext.email;
  
  const span = tracer.startSpan('unhandledrejection', { attributes });
  if (event.reason instanceof Error) span.recordException(event.reason);
  span.end();
});